import modalsReducer from '@/redux/slices/modal-tariff-slice';
import { configureStore } from '@reduxjs/toolkit';
import { createLogger } from 'redux-logger';

import { commonApi } from './api/common-api';
import { permissionApi } from './api/permission-api';
import { rtkQueryApi } from './api/rtk-query-api';
import { productCarouselApi } from './api/shelves-api';
import addArticlesReducer from './slices/addArticlesSlice';
import categoriesTabDataSlice from './slices/categories-data-slice';
import comparisonsTabDataSlice from './slices/comparisons-data-slice';
import gptSlice from './slices/gpt-slice';
import parsingProgressSlice from './slices/parsing-progress-slice';
import phrasesLibraryDataSlice from './slices/phrases-library-data-slice';
import projectArticlesSlice from './slices/project-articles-slice';
import projectPhrasesDataSlice from './slices/project-phrases-data-slice';
import reportProductSlice from './slices/report-product-slice';
import reportProjectSlice from './slices/report-project-slice';
import shelvesTabDataSlice from './slices/shelves-data-slice';
import stocksCacheReducer from './slices/stocks-cache-slice';
import tipsSlice from './slices/tip-slice';
import uiReducer from './slices/ui-slice';
import financeRedirectReducer from './slices/finance-redirect-slice';
import userSlice from './slices/user-slice';
import { projectFoldersApi } from './api/project-folders-api';
import checkedReducer from './slices/checked-slice';
import folderReducer from './slices/folder-slice';

const logger = createLogger({
  collapsed: true,
});

export const store = configureStore({
  reducer: {
    user: userSlice,
    tipsMode: tipsSlice,
    parsingProgress: parsingProgressSlice,
    modals: modalsReducer,
    reportProduct: reportProductSlice,
    reportProject: reportProjectSlice,
    projectPhrasesData: projectPhrasesDataSlice,
    phrasesLibraryData: phrasesLibraryDataSlice,
    comparisonsTabData: comparisonsTabDataSlice,
    shelvesTabData: shelvesTabDataSlice,
    categoriesTabData: categoriesTabDataSlice,
    gpt: gptSlice,
    projectArticles: projectArticlesSlice,
    [rtkQueryApi.reducerPath]: rtkQueryApi.reducer,
    [productCarouselApi.reducerPath]: productCarouselApi.reducer,
    stocksCache: stocksCacheReducer,
    folders: folderReducer,
    checked: checkedReducer,
    addArticles: addArticlesReducer,
    [commonApi.reducerPath]: commonApi.reducer,
    [permissionApi.reducerPath]: permissionApi.reducer,
    ui: uiReducer,
    financeRedirect: financeRedirectReducer,
  },
  middleware: (getDefaultMiddleware) => {
    const defaultMiddleware = getDefaultMiddleware({
      serializableCheck: false,
    });
    const middlewares = defaultMiddleware
      .concat(rtkQueryApi.middleware)
      .concat(productCarouselApi.middleware)
      .concat(projectFoldersApi.middleware)
      .concat(productCarouselApi.middleware)
      .concat(commonApi.middleware)
      .concat(permissionApi.middleware);

    if (import.meta.env.DEV) {
      middlewares.push(logger);
    }

    return middlewares;
  },
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
